import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createContext, useContext, useMemo, useState } from 'react';

// Dark mode forcé — pas de bascule pour le moment
const darkMode = true;

const VALID_FAMILIES = ['Famille', 'Ecole', 'Art', 'Justice', 'Travail', 'Santé', 'Environnement', 'default'];

const ThemeContext = createContext({
  darkMode: true,
  selectedFamily: 'default',
  selectFamily: () => {},
  theme: {},
});

export const ThemeProvider = ({ children }) => {

  // selectedFamily conservé si tu veux l'utiliser plus tard
  // Pour le désactiver complètement, on remplace par : const selectedFamily = 'default';
  const [selectedFamily, setSelectedFamily] = useState('default');

  const selectFamily = (family) => {
    const normalizedFamily = VALID_FAMILIES.includes(family) ? family : 'default';
    setSelectedFamily(normalizedFamily);
  };

  const palette = {
    mode: 'dark',
    background: {
      default: '#0f172a',
      paper: '#1e293b',
      contrast: '#334155',
    },
    text: {
      primary: '#e5e7eb',
      secondary: '#9ca3af',
      contrast: '#ffffff',
    },
    primary: {
      main: '#38bdf8',
      dark: '#015f8a',
      light: '#6ac8ff',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f4d03f',
      dark: '#c49b00',
      light: '#f7dc66',
      contrastText: '#1e293b',
    },
    accent: {
      neon: '#22d3ee',
      glow: '#a78bfa',
      gold: '#B89700',
      blueGlow: '#38bdf8',
      yellow: '#FBBF24',
      matrixGreen: '#00FF41',
    },
    divider: '#ffffff1f',
    grey: {
      900: '#0f172a',
      800: '#1e293b',
      700: '#334155',
      600: '#475569',
      500: '#64748b',
      400: '#94a3b8',
      300: '#d1d5db',
      200: '#e5e7eb',
      100: '#f1f5f9',
    },
    ideation: {
      fonder:   { main: { light: '#df7c55', dark: '#db531d' }, contrastText: '#e4e4e4' },
      etablir:  { main: { light: '#4CAF50', dark: '#2E7D32' }, contrastText: '#e4e4e4' },
      prouver:  { main: { light: '#AB47BC', dark: '#6A1B9A' }, contrastText: '#e4e4e4' },
      synthese: { main: { light: '#B0BEC5', dark: '#78909C' }, contrastText: '#e4e4e4' },
    },
    vibrantColors: {
      progression:   '#ff6f43cc',
      tasks:         '#4bc0c099',
      collaboration: '#ffce5699',
      presets:       '#9966ff99',
      certificates:  '#168840cc',
    },
    family: {
      main: {
        Famille:      '#60a5fa',
        Ecole:        '#34d399',
        Art:          '#f87171',
        Justice:      '#c084fc',
        Travail:      '#2b6cb0',
        Santé:        '#34d399',
        Environnement:'#4ade80',
        default:      '#4b9dea',
      },
      light: {
        Famille:      '#93c5fd',
        Ecole:        '#6ee7b7',
        Art:          '#f87171',
        Justice:      '#d8b4fe',
        Travail:      '#60a5fa',
        Santé:        '#6ee7b7',
        Environnement:'#86efac',
        default:      '#93c5fd',
      },
      dark: {
        Famille:      '#2563eb',
        Ecole:        '#059669',
        Art:          '#dc2626',
        Justice:      '#9333ea',
        Travail:      '#1e40af',
        Santé:        '#059669',
        Environnement:'#16a34a',
        default:      '#2563eb',
      },
      contrastText: '#ffffff',
      pinned: {
        Famille:      '#60a5facf',
        Ecole:        '#34d399cf',
        Art:          '#f87171cf',
        Justice:      '#c084fccf',
        Travail:      '#2b6cb0cf',
        Santé:        '#34d399cf',
        Environnement:'#4ade80cf',
        default:      '#4b9deacf',
      },
      pinnedHover: {
        Famille:      '#60a5fad9',
        Ecole:        '#34d399d9',
        Art:          '#f87171d9',
        Justice:      '#c084fcd9',
        Travail:      '#2b6cb0d9',
        Santé:        '#34d399d9',
        Environnement:'#4ade80d9',
        default:      '#4b9dead9',
      },
    },
    families: {
      main: {
        Famille:      '#60a5fa',
        Ecole:        '#fc8759ff',
        Art:          '#f87171',
        Justice:      '#c084fc',
        Travail:      '#2b6cb0',
        Santé:        '#34d399',
        Environnement:'#4ade80',
        default:      '#4b9dea',
      },
      light: {
        Famille:      '#93c5fd',
        Ecole:        '#6ee7b7',
        Art:          '#f87171',
        Justice:      '#d8b4fe',
        Travail:      '#60a5fa',
        Santé:        '#6ee7b7',
        Environnement:'#86efac',
        default:      '#93c5fd',
      },
      dark: {
        Famille:      '#2563eb',
        Ecole:        '#059669',
        Art:          '#dc2626',
        Justice:      '#9333ea',
        Travail:      '#1e40af',
        Santé:        '#059669',
        Environnement:'#16a34a',
        default:      '#2563eb',
      },
      contrastText: '#ffffff',
      pinned: {
        Famille:      '#60a5facf',
        Ecole:        '#34d399cf',
        Art:          '#f87171cf',
        Justice:      '#c084fccf',
        Travail:      '#2b6cb0cf',
        Santé:        '#34d399cf',
        Environnement:'#4ade80cf',
        default:      '#4b9deacf',
      },
      pinnedHover: {
        Famille:      '#60a5fad9',
        Ecole:        '#34d399d9',
        Art:          '#f87171d9',
        Justice:      '#c084fcd9',
        Travail:      '#2b6cb0d9',
        Santé:        '#34d399d9',
        Environnement:'#4ade80d9',
        default:      '#4b9dead9',
      },
    },
    projectTypeColors: {
      art:          '#ff6f43cc',
      education:    '#36b2eb99',
      social:       '#4bc0c099',
      association:  '#ffce5699',
      other:        '#9966ff99',
    },
    shape: {
      borderRadius: 4,
    },
  };

  const shadows = [
    'none',
    '0px 1px 3px rgba(0,0,0,0.2), 0px 1px 1px rgba(0,0,0,0.14), 0px 2px 1px rgba(0,0,0,0.12)',
    '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px rgba(0,0,0,0.14), 0px 1px 3px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px rgba(0,0,0,0.14), 0px 1px 5px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2), 0px 1px 10px rgba(0,0,0,0.14), 0px 2px 4px rgba(0,0,0,0.12)',
    '0px 6px 10px rgba(0,0,0,0.2), 0px 1px 18px rgba(0,0,0,0.14), 0px 3px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px 1px rgba(0,0,0,0.2), 0px 3px 14px rgba(0,0,0,0.14), 0px 4px 5px rgba(0,0,0,0.12)',
    '0px 9px 12px 1px rgba(0,0,0,0.2), 0px 3px 16px rgba(0,0,0,0.14), 0px 5px 6px rgba(0,0,0,0.12)',
    '0px 12px 17px 2px rgba(0,0,0,0.2), 0px 5px 22px rgba(0,0,0,0.14), 0px 7px 8px rgba(0,0,0,0.12)',
    '0px 16px 24px 2px rgba(0,0,0,0.2), 0px 6px 30px rgba(0,0,0,0.14), 0px 8px 10px rgba(0,0,0,0.12)',
    '0px 24px 38px 3px rgba(0,0,0,0.2), 0px 9px 46px rgba(0,0,0,0.14), 0px 11px 15px rgba(0,0,0,0.12)',
    '0px 30px 60px 4px rgba(0,0,0,0.2), 0px 12px 76px rgba(0,0,0,0.14), 0px 15px 20px rgba(0,0,0,0.12)',
    '0px 36px 80px 5px rgba(0,0,0,0.2), 0px 15px 90px rgba(0,0,0,0.14), 0px 18px 24px rgba(0,0,0,0.12)',
    '0px 48px 100px 6px rgba(0,0,0,0.2), 0px 18px 110px rgba(0,0,0,0.14), 0px 21px 27px rgba(0,0,0,0.12)',
    '0px 60px 120px 7px rgba(0,0,0,0.2), 0px 21px 130px rgba(0,0,0,0.14), 0px 24px 30px rgba(0,0,0,0.12)',
    '0px 72px 140px 8px rgba(0,0,0,0.2), 0px 24px 150px rgba(0,0,0,0.14), 0px 27px 33px rgba(0,0,0,0.12)',
    '0px 84px 160px 9px rgba(0,0,0,0.2), 0px 27px 170px rgba(0,0,0,0.14), 0px 30px 36px rgba(0,0,0,0.12)',
    '0px 96px 180px 10px rgba(0,0,0,0.2), 0px 30px 190px rgba(0,0,0,0.14), 0px 33px 39px rgba(0,0,0,0.12)',
    '0px 108px 200px 11px rgba(0,0,0,0.2), 0px 33px 210px rgba(0,0,0,0.14), 0px 36px 42px rgba(0,0,0,0.12)',
    '0px 120px 220px 12px rgba(0,0,0,0.2), 0px 36px 230px rgba(0,0,0,0.14), 0px 39px 45px rgba(0,0,0,0.12)',
    '0px 132px 240px 13px rgba(0,0,0,0.2), 0px 39px 250px rgba(0,0,0,0.14), 0px 42px 48px rgba(0,0,0,0.12)',
    '0px 144px 260px 14px rgba(0,0,0,0.2), 0px 42px 270px rgba(0,0,0,0.14), 0px 45px 51px rgba(0,0,0,0.12)',
    '0px 156px 280px 15px rgba(0,0,0,0.2), 0px 45px 290px rgba(0,0,0,0.14), 0px 48px 54px rgba(0,0,0,0.12)',
    '0px 168px 300px 16px rgba(0,0,0,0.2), 0px 48px 310px rgba(0,0,0,0.14), 0px 51px 57px rgba(0,0,0,0.12)',
    '0px 180px 320px 17px rgba(0,0,0,0.2), 0px 51px 330px rgba(0,0,0,0.14), 0px 54px 60px rgba(0,0,0,0.12)',
  ];

  const theme = useMemo(() => createTheme({
    selectedFamily,
    palette,
    shadows,
    typography: {
      fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      h1: { fontFamily: "'Orbitron', sans-serif", fontWeight: 400, letterSpacing: '0.05em', color: '#ffffff' },
      h2: { fontFamily: "'Orbitron', sans-serif", fontWeight: 400, letterSpacing: '0.05em', color: '#ffffff' },
      h3: { fontFamily: "'Orbitron', sans-serif", fontWeight: 400, letterSpacing: '0.05em', color: '#ffffff' },
      h4: { fontFamily: "'Orbitron', sans-serif", fontWeight: 300, color: '#ffffff' },
      h5: { fontFamily: "'Orbitron', sans-serif", fontWeight: 300, color: '#ffffff' },
      h6: { fontFamily: "'Orbitron', sans-serif", fontWeight: 300, color: '#ffffff' },
      body1: { fontFamily: "'Roboto', sans-serif", fontSize: '1.1rem', lineHeight: 1.6, fontWeight: 200, color: '#e5e7eb' },
      body2: { fontFamily: "'Roboto', sans-serif", fontSize: '0.95rem', fontWeight: 200, color: '#e5e7eb' },
      caption: { fontFamily: "'Roboto', sans-serif", fontSize: '0.8rem', fontWeight: 200, color: '#9ca3af' },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${palette.divider}`,
            boxShadow: shadows[4],
            background: palette.background.paper,
            borderRadius: 12,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 6px 25px rgba(0,0,0,0.7)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            letterSpacing: '0.05em',
            padding: '10px 20px',
            borderRadius: 8,
            transition: 'all 0.3s ease-in-out',
            backgroundColor: palette.families.main[selectedFamily] || palette.primary.main,
            color: palette.families.contrastText || palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.families.dark[selectedFamily] || palette.primary.dark,
              boxShadow: `0 0 15px ${palette.accent.neon}`,
              transform: 'translateY(-1px)',
            },
          },
          outlined: {
            borderColor: palette.families.main[selectedFamily] || palette.primary.main,
            color: palette.families.main[selectedFamily] || palette.primary.main,
            '&:hover': {
              backgroundColor: `${palette.primary.main}12`,
              borderColor: palette.families.dark[selectedFamily] || palette.primary.dark,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              backgroundColor: palette.background.default,
              '& fieldset': { borderColor: palette.divider },
              '&:hover fieldset': { borderColor: palette.families.main[selectedFamily] || palette.primary.main },
              '&.Mui-focused fieldset': {
                borderColor: palette.families.main[selectedFamily] || palette.primary.main,
                boxShadow: `0 0 8px ${palette.families.main[selectedFamily] || palette.primary.main}4d`,
              },
              '& .MuiInputBase-input': { color: palette.text.primary },
            },
            '& .MuiInputLabel-root': { color: palette.text.secondary },
            '& .MuiInputLabel-root.Mui-focused': { color: palette.families.main[selectedFamily] || palette.primary.main },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { borderRight: `1px solid ${palette.divider}` },
          indicator: { backgroundColor: palette.families.main[selectedFamily] || palette.primary.main },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            color: palette.text.secondary,
            '&.Mui-selected': {
              color: palette.families.main[selectedFamily] || palette.primary.main,
              backgroundColor: `${palette.families.main[selectedFamily] || palette.primary.main}1a`,
            },
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
            transition: 'all 0.3s ease',
          },
        },
      },
    },
  }), [selectedFamily]);

  return (
    <ThemeContext.Provider value={{ darkMode: true, selectFamily, selectedFamily, theme }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};